import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

export enum Breakpoint {
  XS = 'xs',   // 0-575px
  SM = 'sm',   // 576-767px
  MD = 'md',   // 768-991px
  LG = 'lg',   // 992-1199px
  XL = 'xl',   // 1200-1399px
  XXL = 'xxl'  // 1400px+
}

export interface ScreenSize {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ResponsiveBreakpointService implements OnDestroy {

  private screenSizeSubject = new BehaviorSubject<ScreenSize>(this.getCurrentScreenSize());
  private destroy$ = new Subject<void>();

  // Main observable for screen size
  public readonly screenSize$: Observable<ScreenSize> = this.screenSizeSubject.asObservable();

  // Observable for breakpoint changes only
  public readonly breakpoint$: Observable<Breakpoint> = this.screenSize$.pipe(
    map(size => size.breakpoint),
    distinctUntilChanged()
  );

  // Observable for mobile state changes
  public readonly isMobile$: Observable<boolean> = this.screenSize$.pipe(
    map(size => size.isMobile),
    distinctUntilChanged()
  );

  // Observable for desktop state changes
  public readonly isDesktop$: Observable<boolean> = this.screenSize$.pipe(
    map(size => size.isDesktop),
    distinctUntilChanged()
  );

  // Observable for tablet state changes
  public readonly isTablet$: Observable<boolean> = this.screenSize$.pipe(
    map(size => size.isTablet),
    distinctUntilChanged()
  );

  constructor(private ngZone: NgZone) {
    this.setupResizeListener();
  }

  /**
   * Setup resize listener using NgZone for proper change detection
   */
  private setupResizeListener(): void {
    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(150), // Debounce resize events
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          // Run inside Angular zone to trigger change detection
          this.ngZone.run(() => {
            const newSize = this.getCurrentScreenSize();
            this.screenSizeSubject.next(newSize);
          });
        });
    });
  }

  /**
   * Get current screen size information
   */
  private getCurrentScreenSize(): ScreenSize {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const breakpoint = this.getBreakpoint(width);

    return {
      width,
      height,
      breakpoint,
      isMobile: width < 768,        // xs and sm
      isTablet: width >= 768 && width < 992, // md
      isDesktop: width >= 992       // lg, xl, xxl
    };
  }

  /**
   * Determine breakpoint based on width
   */
  // eslint-disable-next-line complexity
  private getBreakpoint(width: number): Breakpoint {
    if (width < 576) return Breakpoint.XS;
    if (width < 768) return Breakpoint.SM;
    if (width < 992) return Breakpoint.MD;
    if (width < 1200) return Breakpoint.LG;
    if (width < 1400) return Breakpoint.XL;
    return Breakpoint.XXL;
  }

  /**
   * Get current screen size (synchronous)
   */
  public getCurrentSize(): ScreenSize {
    return this.screenSizeSubject.value;
  }

  /**
   * Check if current screen is mobile (synchronous)
   */
  public isMobile(): boolean {
    return this.screenSizeSubject.value.isMobile;
  }

  /**
   * Check if current screen is tablet (synchronous)
   */
  public isTablet(): boolean {
    return this.screenSizeSubject.value.isTablet;
  }

  /**
   * Check if current screen is desktop (synchronous)
   */
  public isDesktop(): boolean {
    return this.screenSizeSubject.value.isDesktop;
  }

  /**
   * Get current breakpoint (synchronous)
   */
  public getCurrentBreakpoint(): Breakpoint {
    return this.screenSizeSubject.value.breakpoint;
  }

  /**
   * Check if screen matches specific breakpoint
   */
  public isBreakpoint(breakpoint: Breakpoint): boolean {
    return this.getCurrentBreakpoint() === breakpoint;
  }

  /**
   * Check if screen is at or above breakpoint
   */
  public isBreakpointUp(breakpoint: Breakpoint): boolean {
    const breakpoints = [
      Breakpoint.XS,
      Breakpoint.SM,
      Breakpoint.MD,
      Breakpoint.LG,
      Breakpoint.XL,
      Breakpoint.XXL
    ];
    const currentIndex = breakpoints.indexOf(this.getCurrentBreakpoint());
    const targetIndex = breakpoints.indexOf(breakpoint);
    return currentIndex >= targetIndex;
  }

  /**
   * Check if screen is at or below breakpoint
   */
  public isBreakpointDown(breakpoint: Breakpoint): boolean {
    const breakpoints = [
      Breakpoint.XS,
      Breakpoint.SM,
      Breakpoint.MD,
      Breakpoint.LG,
      Breakpoint.XL,
      Breakpoint.XXL
    ];
    const currentIndex = breakpoints.indexOf(this.getCurrentBreakpoint());
    const targetIndex = breakpoints.indexOf(breakpoint);
    return currentIndex <= targetIndex;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.screenSizeSubject.complete();
  }
}
